import { generateUuid } from './uuid.js'
import { fetchWidgetLeadFormById } from './leadFormsApi.js'

/**
 * Standardise an inbound message so message-rendering code can rely on
 * stable fields. Mirrors legacy `chatUtils.standardizeMessage` so the
 * existing per-message callbacks (button click, list select) keep
 * working when wired to the streaming transport.
 */
export function standardizeMessage(msg) {
  const text =
    msg.text ??
    (typeof msg.message === 'string' ? msg.message : undefined) ??
    (typeof msg.body === 'string' ? msg.body : undefined) ??
    ''
  return {
    ...msg,
    text,
    time: msg.time || new Date().toJSON(),
    visible: msg.visible === false ? false : true,
    id: msg.id || generateUuid(),
    attachment: msg.attachment || {},
  }
}

/**
 * "Sage" mode (web-AI agents) returns list messages as inline product /
 * page suggestions rather than a separate carousel. We convert them
 * into a flat suggestion shape that overlay/slide-out layouts both
 * consume.
 */
export function processSageListMessage(msg, conf) {
  if (conf?.isWebAi && msg.type === 'list' && Array.isArray(msg.elements)) {
    return msg.elements.map((el) => ({
      type: el.type || 'product',
      title: el.title,
      subtitle: el.subtitle,
      excerpt: el.excerpt,
      image: el.image_url || el.image,
      image_url: el.image_url || el.image,
      buttons: el.buttons,
      url: el.url || el.item_url,
      add_to_cart_url: el.add_to_cart_url,
      price: el.price,
      sale_price: el.sale_price,
    }))
  }
  return []
}

/**
 * Combine dynamic streaming-suggestions with admin-configured CTAs and
 * lead forms (which arrive on `conf` from the widget config endpoint).
 * Strip order: page/product (and other cards) first, then order status,
 * then contact forms, then CTA banners.
 */
/** Legacy knowledge `page_type` values (ochatbot `Constants.php`). */
const PAGE_TYPE_PAGE = 0
const PAGE_TYPE_PRODUCT = 1
const PAGE_TYPE_DOCUMENT = 2
const PAGE_TYPE_FAQ = 3
const PAGE_TYPE_AI_DOC = 4

/** FAQ / document RAG hits render in the bubble only, not the suggestions strip. */
export function isKnowledgeEntryPageType(pageType) {
  return (
    pageType === PAGE_TYPE_DOCUMENT ||
    pageType === PAGE_TYPE_FAQ ||
    pageType === PAGE_TYPE_AI_DOC
  )
}

function citationDefaultTitle(src) {
  return (
    (typeof src?.title === 'string' && src.title.trim() !== '' && src.title) ||
    'Source'
  )
}

/**
 * FAQ (and optional document) quick-replies configured in the builder.
 * Accepts backend `buttons` JSON or legacy `faq_button_data`.
 */
function citationAttachedButtons(src) {
  if (!src || typeof src !== 'object') return []
  const fallbackTitle = citationDefaultTitle(src)
  const attached = []

  if (Array.isArray(src.buttons)) {
    for (const btn of src.buttons) {
      if (!btn || typeof btn !== 'object') continue
      const url = typeof btn.url === 'string' ? btn.url.trim() : ''
      if (!url) continue
      const title =
        (typeof btn.button_text === 'string' && btn.button_text.trim()) ||
        (typeof btn.title === 'string' && btn.title.trim()) ||
        fallbackTitle
      attached.push({ type: 'web_url', title, url })
    }
  }

  const legacy = src.faq_button_data
  if (
    attached.length === 0 &&
    legacy &&
    typeof legacy === 'object' &&
    typeof legacy.url === 'string' &&
    legacy.url.trim()
  ) {
    const url = legacy.url.trim()
    const title =
      (typeof legacy.button_text === 'string' && legacy.button_text.trim()) ||
      fallbackTitle
    attached.push({ type: 'web_url', title, url })
  }

  return attached
}

/**
 * Maps RAG citation payloads from the streaming `citations` SSE event (or JSON
 * `citations` array) into `SuggestionsStrip` items (`type` `product` | `page`).
 * FAQ and document hits are omitted (v1 {@code OchatbotAIModel} parity).
 */
export function citationSourcesToSuggestions(sources, conf = null) {
  if (!Array.isArray(sources) || sources.length === 0) return []
  const isWebAi = conf?.isWebAi === true
  return sources.flatMap((src, index) => {
    if (!src || typeof src !== 'object') return []
    const pageType = src.page_type
    if (isKnowledgeEntryPageType(pageType)) return []
    // v1 `OchatbotAIModel`: page cards only when `isWebAi`; otherwise URL buttons.
    if (pageType === PAGE_TYPE_PAGE && !isWebAi) return []
    const hasCart =
      typeof src.add_to_cart_url === 'string' && src.add_to_cart_url.trim() !== ''
    const isProduct =
      pageType === PAGE_TYPE_PRODUCT ||
      hasCart ||
      (src.ecommerce_id != null &&
        src.ecommerce_id !== '' &&
        String(src.product_id || '').trim() !== '')
    const subtitle =
      (typeof src.snippet === 'string' && src.snippet.trim()) || ''
    const title =
      (typeof src.title === 'string' && src.title.trim() !== '' && src.title) ||
      'Source'
    const url = typeof src.url === 'string' ? src.url : ''
    const imageUrl =
      (typeof src.image_url === 'string' && src.image_url) ||
      (typeof src.imageUrl === 'string' && src.imageUrl) ||
      (typeof src.image === 'string' && src.image) ||
      (typeof src.featured_image === 'string' && src.featured_image) ||
      (typeof src.featuredImage === 'string' && src.featuredImage) ||
      (typeof src.product_image === 'string' && src.product_image) ||
      (typeof src.productImage === 'string' && src.productImage) ||
      (typeof src.thumbnail_url === 'string' && src.thumbnail_url) ||
      (typeof src.thumbnailUrl === 'string' && src.thumbnailUrl) ||
      (typeof src.thumbnail === 'string' && src.thumbnail) ||
      ''
    return [
      {
        type: isProduct ? 'product' : 'page',
        id: src.id != null ? String(src.id) : `citation-${index}`,
        title,
        subtitle,
        excerpt: subtitle,
        url,
        item_url: url,
        add_to_cart_url:
          typeof src.add_to_cart_url === 'string' ? src.add_to_cart_url : '',
        image_url: imageUrl,
        buttons: url ? [{ url }] : [],
        ...(typeof src.price === 'string' && src.price.trim() !== ''
          ? { price: src.price.trim() }
          : {}),
        ...(typeof src.sale_price === 'string' && src.sale_price.trim() !== ''
          ? { sale_price: src.sale_price.trim() }
          : {}),
      },
    ]
  })
}

/**
 * BotMan-style quick-reply for the agent default menu conversation (v1
 * `RETURN_TO_MENU` postback on citation replies).
 */
export function buildReturnToMenuButton(conf) {
  const convId = conf?.defaultMenuConversationId
  if (convId == null || !Number.isFinite(Number(convId)) || Number(convId) <= 0) {
    return null
  }
  const label =
    (typeof conf?.returnToMenuLabel === 'string' && conf.returnToMenuLabel.trim()) ||
    'Return to Menu'
  return {
    type: 'postback',
    title: label,
    payload: `question-trigger-${Number(convId)}`,
  }
}

/**
 * Maps RAG citation rows into bubble quick-replies (web links + optional
 * return-to-menu on LLM turns only).
 */
function citationMessageButtonKey(btn) {
  if (!btn || typeof btn !== 'object') return ''
  if (btn.type === 'postback') {
    return `postback:${String(btn.payload || btn.title || '').trim()}`
  }
  const url = typeof btn.url === 'string' ? btn.url.trim().toLowerCase() : ''
  const title = typeof btn.title === 'string' ? btn.title.trim().toLowerCase() : ''
  return `web:${url || title}`
}

function pushCitationMessageButtons(buttons, max, nextButtons) {
  const seen = new Set(buttons.map(citationMessageButtonKey).filter(Boolean))
  for (const btn of nextButtons) {
    if (buttons.length >= max) break
    const key = citationMessageButtonKey(btn)
    if (key && seen.has(key)) continue
    if (key) seen.add(key)
    buttons.push(btn)
  }
}

export function citationSourcesToMessageButtons(sources, conf = null, options = {}) {
  if (!Array.isArray(sources) || sources.length === 0) return []
  const max =
    conf != null && typeof conf.maxSuggestions === 'number' && conf.maxSuggestions > 0
      ? conf.maxSuggestions
      : 4
  const buttons = []
  const seenFaqEcommerceIds = new Set()
  for (const src of sources) {
    if (buttons.length >= max) break
    if (!src || typeof src !== 'object') continue
    const pageType = src.page_type

    if (pageType === PAGE_TYPE_FAQ) {
      const faqId =
        src.ecommerce_id != null && src.ecommerce_id !== ''
          ? String(src.ecommerce_id)
          : ''
      if (faqId && seenFaqEcommerceIds.has(faqId)) continue
      if (faqId) seenFaqEcommerceIds.add(faqId)
      const attached = citationAttachedButtons(src)
      if (attached.length > 0) {
        pushCitationMessageButtons(buttons, max, attached)
        continue
      }
      const url = typeof src.url === 'string' ? src.url.trim() : ''
      if (!url) continue
      buttons.push({
        type: 'web_url',
        title: String(citationDefaultTitle(src)),
        url,
      })
      continue
    }

    if (pageType === PAGE_TYPE_DOCUMENT) {
      pushCitationMessageButtons(buttons, max, citationAttachedButtons(src))
      continue
    }

    if (pageType === PAGE_TYPE_AI_DOC) {
      const attached = citationAttachedButtons(src)
      if (attached.length > 0) {
        pushCitationMessageButtons(buttons, max, attached)
        continue
      }
      const url = typeof src.url === 'string' ? src.url.trim() : ''
      if (!url) continue
      buttons.push({
        type: 'web_url',
        title: citationDefaultTitle(src),
        url,
      })
      continue
    }

    if (isKnowledgeEntryPageType(pageType)) {
      pushCitationMessageButtons(buttons, max, citationAttachedButtons(src))
      continue
    }

    const url = typeof src.url === 'string' ? src.url.trim() : ''
    if (!url) continue
    buttons.push({
      type: 'web_url',
      title: citationDefaultTitle(src),
      url,
    })
  }
  if (options.includeReturnToMenu === true) {
    const menu = buildReturnToMenuButton(conf)
    if (menu) buttons.push(menu)
  }
  return buttons
}

/**
 * Attach strip cards and bubble quick-replies for a citation payload.
 */
export function applyCitationAffordances(sources, conf = null, options = {}) {
  const suggestions = citationSourcesToSuggestions(sources, conf)
  const buttons = citationSourcesToMessageButtons(sources, conf, options)
  return {
    citations: sources,
    suggestions,
    buttons,
  }
}

/**
 * Admin CTAs / lead forms: pivot `is_active` gates the AI-display link.
 * Published vs draft is enforced when bootstrap builds `activeCtas` /
 * `activeLeadForms`; do not re-filter on `status` here — PHP casts
 * `null` status to `false`, which incorrectly dropped valid forms/media.
 */
function isRenderableAdminStripItem(item) {
  if (!item || typeof item !== 'object') return false
  const link = item.is_active
  if (
    link === false ||
    link === 0 ||
    link === '0' ||
    link === 'false'
  ) {
    return false
  }
  return true
}

function filterRenderableAdminCtas(ctas) {
  if (!Array.isArray(ctas)) return []
  return ctas.filter(isRenderableAdminStripItem)
}

function filterRenderableAdminLeadForms(forms) {
  if (!Array.isArray(forms)) return []
  return forms.filter(isRenderableAdminStripItem)
}

function firstNonEmptyArray(...candidates) {
  for (const list of candidates) {
    if (Array.isArray(list) && list.length > 0) return list
  }
  for (const list of candidates) {
    if (Array.isArray(list)) return list
  }
  return []
}

/**
 * Active CTAs / lead forms for the URL-targeted AI Display (bootstrap
 * `activeCtas` / `activeLeadForms`). Prefer live context, then conf mirrors.
 */
export function resolveActiveAdminCtas(conf, activeCtas) {
  return firstNonEmptyArray(
    activeCtas,
    conf?.activeCtas,
    conf?.ctas,
  )
}

export function resolveActiveAdminLeadForms(conf, activeLeadForms) {
  return firstNonEmptyArray(
    activeLeadForms,
    conf?.activeLeadForms,
    conf?.leadForms,
  )
}

/** Flatten CTA `config` onto strip fields (`title`, `url`, `image_url`). */
export function normalizeCtaForSuggestionsStrip(cta) {
  if (!cta || typeof cta !== 'object') return cta
  const cfg =
    cta.config && typeof cta.config === 'object' && !Array.isArray(cta.config)
      ? cta.config
      : {}
  const type = cta.type || 'banner'
  const isVisualMedia =
    type === 'banner' || type === 'image' || type === 'video'
  const title =
    (typeof cta.title === 'string' && cta.title.trim()) ||
    (typeof cfg.headline === 'string' && cfg.headline.trim()) ||
    (!isVisualMedia && typeof cta.name === 'string' && cta.name.trim()) ||
    ''
  const excerpt =
    (typeof cta.excerpt === 'string' && cta.excerpt) ||
    (typeof cfg.description === 'string' && cfg.description) ||
    ''
  const url =
    (typeof cta.url === 'string' && cta.url.trim()) ||
    (typeof cfg.url === 'string' && cfg.url.trim()) ||
    ''
  const imageUrl =
    (typeof cta.image_url === 'string' && cta.image_url) ||
    (typeof cfg.bannerImage === 'string' && cfg.bannerImage) ||
    (typeof cfg.image === 'string' && cfg.image) ||
    ''
  return {
    ...cta,
    type: cta.type || 'banner',
    title,
    excerpt,
    url,
    image_url: imageUrl,
    buttons: url ? [{ url, title: title || 'Open' }] : cta.buttons,
  }
}

/**
 * Normalize a URL for strip dedup (v1 / {@code WidgetEmbeddingsUrlLookup} parity).
 */
export function normalizeStripUrlKey(url) {
  if (typeof url !== 'string') return ''
  let s = url.trim()
  const q = s.indexOf('?')
  if (q !== -1) s = s.slice(0, q)
  s = s.replace(/\/$/, '')
  s = s.replace(/\.html$/i, '')
  return s.toLowerCase()
}

/** Unique `web_url` targets from a BotMan `buttons` payload. */
export function collectBotWebUrlButtonUrls(buttons) {
  if (!Array.isArray(buttons)) return []
  const seen = new Set()
  const out = []
  for (const btn of buttons) {
    if (!btn || btn.type !== 'web_url') continue
    const url = typeof btn.url === 'string' ? btn.url.trim() : ''
    if (!url) continue
    const key = normalizeStripUrlKey(url)
    if (!key || seen.has(key)) continue
    seen.add(key)
    out.push(url)
  }
  return out
}

/** Normalized URL keys already represented on the suggestions strip. */
export function suggestionStripItemUrlKeys(suggestions) {
  const keys = new Set()
  if (!Array.isArray(suggestions)) return keys
  for (const item of suggestions) {
    if (!item || typeof item !== 'object') continue
    const urls = []
    if (typeof item.url === 'string') urls.push(item.url)
    if (typeof item.item_url === 'string') urls.push(item.item_url)
    if (Array.isArray(item.buttons)) {
      for (const btn of item.buttons) {
        if (typeof btn?.url === 'string') urls.push(btn.url)
      }
    }
    for (const u of urls) {
      const key = normalizeStripUrlKey(u)
      if (key) keys.add(key)
    }
  }
  return keys
}

/**
 * Map embedding rows to strip cards, omitting URLs already on the strip
 * (e.g. Bedrock citation cards on the same turn).
 */
export function citationRowsToStripSuggestions(
  rows,
  conf = null,
  existingSuggestions = [],
) {
  if (!Array.isArray(rows) || rows.length === 0) return []
  const seen = suggestionStripItemUrlKeys(existingSuggestions)
  const filtered = []
  for (const row of rows) {
    if (!row || typeof row !== 'object') continue
    const url = typeof row.url === 'string' ? row.url : ''
    const key = normalizeStripUrlKey(url)
    if (key && seen.has(key)) continue
    if (key) seen.add(key)
    filtered.push(row)
  }
  return citationSourcesToSuggestions(filtered, conf)
}

function isLeadFormTriggerPayload(payload) {
  return typeof payload === 'string' && payload.startsWith('lead-form-trigger-')
}

function isOrderStatusOpenPayload(payload) {
  if (typeof payload !== 'string') return false
  const trimmed = payload.trim()
  return trimmed === 'order-status-open' || trimmed.startsWith('order-status-open')
}

/** Order status hamburger menu entry (requires feature enabled in agent config). */
export function isOrderStatusMenuVisible(conf) {
  const os = conf?.orderStatus
  if (!os || os.enabled !== true) return false
  const menu = conf?.menuEcommerce
  if (menu && menu.showOrderStatus === false) return false
  return true
}

/** Global order-status card injected into the suggestions strip/panel. */
export function isOrderStatusSuggestionCardVisible(conf) {
  if (!isOrderStatusMenuVisible(conf)) return false
  return conf.orderStatus.showSuggestionStrip !== false
}

/**
 * Maps engagement-question postbacks (lead form + order status) from a BotMan
 * `buttons` payload into `SuggestionsStrip` cards. Other button kinds stay in
 * the bubble only (web_url embedding promotion is handled separately).
 */
export function engagementActionButtonsToStripSuggestions(
  buttons,
  conf = null,
  activeLeadForms = [],
) {
  if (!Array.isArray(buttons) || buttons.length === 0) return []
  const items = []
  const seenLeadFormIds = new Set()
  let orderStatusAdded = false

  for (const button of buttons) {
    if (!button || typeof button !== 'object') continue
    const payload = button.payload

    if (isLeadFormTriggerPayload(payload)) {
      const formId = String(payload).replace(/^lead-form-trigger-/, '')
      if (!formId || seenLeadFormIds.has(formId)) continue
      seenLeadFormIds.add(formId)

      const forms = filterRenderableAdminLeadForms(
        resolveActiveAdminLeadForms(conf, activeLeadForms),
      )
      const found = forms.find((f) => String(f.id) === formId)
      if (found) {
        items.push({ ...found, type: 'leadform' })
        continue
      }

      const label =
        (typeof button.title === 'string' && button.title.trim()) || 'Contact'
      items.push({
        type: 'leadform',
        id: formId,
        title: label,
        config: {
          buttonText: label,
          headline: label,
        },
      })
      continue
    }

    if (!orderStatusAdded && isOrderStatusOpenPayload(payload)) {
      const os = conf?.orderStatus
      if (!os || os.enabled !== true) continue
      orderStatusAdded = true
      const submitLabel =
        (typeof button.title === 'string' && button.title.trim()) ||
        (typeof os.submitLabel === 'string' && os.submitLabel.trim()) ||
        'Look up order'
      const cardImageUrl =
        typeof os.cardImageUrl === 'string' && os.cardImageUrl.trim()
          ? os.cardImageUrl.trim()
          : ''
      items.push({
        type: 'orderstatus',
        id: 'widget-order-status',
        title: os.formTitle || 'Order status',
        excerpt:
          os.formSubtitle || 'Look up your order with your order number.',
        submitLabel,
        image_url: cardImageUrl || undefined,
      })
    }
  }

  return orderStripSuggestionItems(items)
}

/**
 * Engagement strip cards built before a form is fetched only carry button
 * label text — not template/fields. Message quick-replies fetch by id on
 * click; the strip must resolve the same payload for display.
 */
export function isEngagementLeadFormStub(item) {
  if (!item || item.type !== 'leadform') return false
  if (item.template) return false
  if (item.settings && typeof item.settings === 'object') return false
  const fields = item.config?.fields
  if (Array.isArray(fields) && fields.length > 0) return false
  return true
}

/**
 * Replace engagement lead-form stub cards with full library payloads fetched
 * by id (agent-wide, not limited to display-active pivot rows).
 */
export async function enrichEngagementLeadFormStripItems(
  items,
  {
    apiBaseUrl,
    agentId,
    masterKey,
    fetchLeadFormById: fetchFn,
    onFormsResolved,
  } = {},
) {
  if (!Array.isArray(items) || items.length === 0) return items

  const stubIds = [
    ...new Set(
      items
        .filter(isEngagementLeadFormStub)
        .map((item) => String(item.id))
        .filter(Boolean),
    ),
  ]
  if (stubIds.length === 0) return items

  const fetchLeadFormById = fetchFn || fetchWidgetLeadFormById

  const fetchedById = new Map()

  await Promise.all(
    stubIds.map(async (formId) => {
      try {
        const list = await fetchLeadFormById({
          apiBaseUrl,
          agentId,
          masterKey,
          formId,
        })
        const form = Array.isArray(list)
          ? list.find((f) => String(f.id) === formId) || list[0]
          : null
        if (form) {
          fetchedById.set(formId, { ...form, type: 'leadform' })
        }
      } catch (err) {
        console.warn(
          'enrichEngagementLeadFormStripItems: lead form lookup failed',
          formId,
          err,
        )
      }
    }),
  )

  if (fetchedById.size === 0) return items

  if (typeof onFormsResolved === 'function') {
    onFormsResolved([...fetchedById.values()])
  }

  return items.map((item) => {
    if (!isEngagementLeadFormStub(item)) return item
    return fetchedById.get(String(item.id)) || item
  })
}

function partitionStripSuggestionItems(items) {
  const content = []
  const orderStatus = []
  const leadForms = []
  for (const item of items) {
    if (!item || typeof item !== 'object') {
      content.push(item)
      continue
    }
    const t = item.type
    if (t === 'orderstatus') orderStatus.push(item)
    else if (t === 'leadform') leadForms.push(item)
    else content.push(item)
  }
  return { content, orderStatus, leadForms }
}

/** Page/product cards first; order status before contact forms. */
export function orderStripSuggestionItems(items) {
  if (!Array.isArray(items) || items.length === 0) return []
  const { content, orderStatus, leadForms } = partitionStripSuggestionItems(items)
  return [...content, ...orderStatus, ...leadForms]
}

/**
 * Stable identity for strip dedup. Engagement cards are persisted on
 * `message.suggestions` and may be re-derived from `message.buttons` when
 * rendering inline strips — one key per order-status card and per lead form id.
 */
export function stripSuggestionItemDedupeKey(item) {
  if (!item || typeof item !== 'object') return null
  const type = item.type
  if (type === 'orderstatus') return 'orderstatus'
  if (type === 'leadform' && item.id != null) return `leadform:${String(item.id)}`
  const urls = []
  if (typeof item.url === 'string') urls.push(item.url)
  if (typeof item.item_url === 'string') urls.push(item.item_url)
  if (Array.isArray(item.buttons)) {
    for (const btn of item.buttons) {
      if (typeof btn?.url === 'string') urls.push(btn.url)
    }
  }
  for (const url of urls) {
    const key = normalizeStripUrlKey(url)
    if (key) return `url:${key}`
  }
  if (item.id != null) return `${type || 'item'}:${String(item.id)}`
  return null
}

/** Collapse duplicate strip cards while preserving first-seen order. */
export function dedupeStripSuggestionItems(items) {
  if (!Array.isArray(items) || items.length === 0) return []
  const seen = new Set()
  const out = []
  for (const item of items) {
    const key = stripSuggestionItemDedupeKey(item)
    if (key) {
      if (seen.has(key)) continue
      seen.add(key)
    }
    out.push(item)
  }
  return out
}

function buildSyntheticOrderStatusStripItem(orderStatusConf) {
  const submitLabel =
    typeof orderStatusConf.submitLabel === 'string' &&
    orderStatusConf.submitLabel.trim()
      ? orderStatusConf.submitLabel.trim()
      : 'Look up order'
  const cardImageUrl =
    typeof orderStatusConf.cardImageUrl === 'string' &&
    orderStatusConf.cardImageUrl.trim()
      ? orderStatusConf.cardImageUrl.trim()
      : ''
  return {
    type: 'orderstatus',
    id: 'widget-order-status',
    title: orderStatusConf.formTitle || 'Order status',
    excerpt:
      orderStatusConf.formSubtitle ||
      'Look up your order with your order number.',
    submitLabel,
    image_url: cardImageUrl || undefined,
  }
}

/**
 * Per-message dynamic rows for inline suggestion strips (slide-out / mobile).
 * Strip content comes from citations, prelude hints, embedding-resolved cards
 * on `message.suggestions`, and engagement lead-form / order-status buttons.
 */
export function resolveMessageStripDynamics(
  message,
  { conf = null, activeLeadForms = [] } = {},
) {
  if (!message || typeof message !== 'object') return []
  const fromSuggestions = Array.isArray(message.suggestions)
    ? message.suggestions
    : []
  const fromButtons = engagementActionButtonsToStripSuggestions(
    message.buttons,
    conf,
    activeLeadForms,
  )
  if (fromButtons.length === 0) return fromSuggestions
  return orderStripSuggestionItems(
    dedupeStripSuggestionItems([...fromSuggestions, ...fromButtons]),
  )
}

/** Suppress global order-status card when rendering a historical bot turn. */
export function confForPerMessageSuggestionStrip(conf) {
  if (!conf?.orderStatus) return conf
  return {
    ...conf,
    orderStatus: {
      ...conf.orderStatus,
      showSuggestionStrip: false,
    },
  }
}

/**
 * Items for an inline strip under one bot bubble. Admin CTAs, lead forms, and
 * order-status cards attach only on the latest bot turn (`includeAdminItems`).
 */
export function combineInlineStripForMessage(
  message,
  { activeCtas = [], activeLeadForms = [], conf = null, includeAdminItems = false } = {},
) {
  const dynamic = resolveMessageStripDynamics(message, {
    conf,
    activeLeadForms,
  })
  if (includeAdminItems) {
    return combineSuggestions(dynamic, activeCtas, activeLeadForms, conf)
  }
  return combineSuggestions(
    dynamic,
    [],
    [],
    confForPerMessageSuggestionStrip(conf),
  )
}

/**
 * Dynamic strip rows for the latest bot turn. Overlay split column mirrors the
 * last completed bot bubble instead of relying only on `ChatLayoutContext`
 * (which can be cleared mid-stream).
 */
export function resolveStripDynamicSuggestions(
  messages,
  contextSuggestions,
  conf = null,
  activeLeadForms = [],
) {
  const ctx = Array.isArray(contextSuggestions) ? contextSuggestions : []
  if (!Array.isArray(messages) || messages.length === 0) return ctx

  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const m = messages[i]
    if (!m || m.from !== 'chatbot' || m.visible === false) continue
    if (m.type === 'typing_indicator' || m.streaming) continue

    const fromMessage = resolveMessageStripDynamics(m, {
      conf,
      activeLeadForms,
    })
    if (fromMessage.length > 0) return fromMessage

    break
  }

  return ctx
}

export function combineSuggestions(
  suggestions,
  activeCtas,
  activeLeadForms,
  conf = null,
) {
  const ctas = filterRenderableAdminCtas(
    resolveActiveAdminCtas(conf, activeCtas),
  ).map(normalizeCtaForSuggestionsStrip)
  const leadForms = filterRenderableAdminLeadForms(
    resolveActiveAdminLeadForms(conf, activeLeadForms),
  )
  const max =
    conf != null && typeof conf.maxSuggestions === 'number' && conf.maxSuggestions > 0
      ? conf.maxSuggestions
      : null
  const dynamic = Array.isArray(suggestions) ? suggestions : []
  const cappedDynamic =
    max != null && dynamic.length > max ? dynamic.slice(0, max) : dynamic

  const { content, orderStatus, leadForms: dynamicLeadForms } =
    partitionStripSuggestionItems(cappedDynamic)

  const dynamicLeadFormIds = new Set(
    dynamicLeadForms
      .filter((item) => item.id != null)
      .map((item) => String(item.id)),
  )
  const adminLeadForms = leadForms
    .filter((f) => !dynamicLeadFormIds.has(String(f.id)))
    .map((f) => ({ ...f, type: 'leadform' }))

  const orderStatusItems = [...orderStatus]
  if (
    orderStatusItems.length === 0 &&
    conf &&
    isOrderStatusSuggestionCardVisible(conf)
  ) {
    orderStatusItems.push(buildSyntheticOrderStatusStripItem(conf.orderStatus))
  }

  return dedupeStripSuggestionItems([
    ...content,
    ...orderStatusItems,
    ...dynamicLeadForms,
    ...adminLeadForms,
    ...ctas,
  ])
}

/**
 * Mobile and slide-out layouts attach suggestions inline under the
 * previous bot message instead of rendering them as a separate strip.
 * Customers can override per-conf via `ometricsAI.inlineSuggestions`.
 */
export function shouldUseInlineSuggestions(conf, layoutType) {
  if (conf?.ometricsAI?.inlineSuggestions !== undefined) {
    return Boolean(conf.ometricsAI.inlineSuggestions)
  }
  return layoutType === 'mobile' || layoutType === 'slide-out'
}

/**
 * Panel-bottom and under-message suggestion strips. Overlay / expand side
 * column always shows and must not use this gate. Message quick-replies are
 * independent of strip visibility.
 */
export function shouldShowSuggestionsStrip(conf) {
  return conf?.showSuggestionsStrip !== false
}

/**
 * Append query flags so {@code routes/botman.php} classifies the click the
 * same way as {@code OchatbotModel::addURLTracking} (ecommerce view / add).
 */
export function withOchatbotEcommerceViewTracking(url) {
  if (url == null || typeof url !== 'string' || !url.trim()) return url
  const u = url.trim()
  if (u.includes('fromOchatbotEcommerceView=1')) return u
  const sep = u.includes('?') ? '&' : '?'
  return `${u}${sep}fromOchatbotEcommerceView=1`
}

export function withOchatbotEcommerceAddToCartTracking(url) {
  if (url == null || typeof url !== 'string' || !url.trim()) return url
  const u = url.trim()
  if (u.includes('fromOchatbotEcommerceAddToCart=1')) return u
  const sep = u.includes('?') ? '&' : '?'
  return `${u}${sep}fromOchatbotEcommerceAddToCart=1`
}
